import {Expose, Type} from "class-transformer";
import {RequisitePsystemTargetType} from "@enums/RequisitePsystemTargetType";
import {RequisiteAddressType} from "@enums/RequisiteAddressType";
import PaymentSystemOperation from "@models/operations/components/PaymentSystemOperation";
import RequisitePsystem from "@models/requisite/RequisitePsystem";

export default class Requisite {
	@Expose({name: "id"})
	private _id: number | null = null;
	@Expose({name: "name"})
	private _name: string = '';
	@Expose({name: "address"})
	private _address: string = '';
	@Expose({name: "addressTypeId"})
	private _addressTypeId: number | null = null;
	@Expose({name: "addressType"})
	private _addressType: RequisiteAddressType | null = RequisiteAddressType.CARD;
	@Expose({name: "comment"})
	private _comment: string = '';
	@Expose({name: "currencyId"})
	private _currencyId: number | null = null;
	@Expose({name: "isApproved"})
	private _isApproved: boolean = false;
	@Expose({name: "isDefault"})
	private _isDefault: boolean = false;
	@Expose({name: "createdAt"})
	private _createdAt: string | null = null;
	@Expose({name: "psystem"})
	@Type(() => RequisitePsystem)
	private _psystem: RequisitePsystem | null = new RequisitePsystem();

	constructor({
    id = null,
    name = '',
    address = '',
    addressTypeId = null,
    addressType = RequisiteAddressType.CARD,
    comment = '',
    currencyId = null,
    isApproved = false,
    isDefault = false,
    createdAt = null,
    psystem = new RequisitePsystem(),
  }: Partial<Requisite> & { psystem?: RequisitePsystem | null } = {}) {
		this._id = id;
		this._name = name;
		this._address = address;
		this._addressTypeId = addressTypeId;
		this._addressType = addressType;
		this._comment = comment;
		this._currencyId = currencyId;
		this._isApproved = isApproved;
		this._isDefault = isDefault;
		this._createdAt = createdAt;
		this._psystem = psystem;
	}

	public isNew(): boolean {
		return this._id === null;
	}

	public getType(): RequisitePsystemTargetType | undefined {
		return this.psystem?.targetType;
	}

	public static build(psystem: RequisitePsystem | null) {
		return new Requisite({
				id: null,
				name: '',
				address: '',
				addressTypeId: psystem?.addressTypes[0] || null,
				addressType: RequisiteAddressType.CARD,
				comment: '',
				currencyId: 1,
				isApproved: false,
				isDefault: false,
				createdAt: "",
				psystem,
			}
		);
	}

	public clone(): Requisite {
		return new Requisite({
			id: this._id,
			name: this._name,
			address: this._address,
			addressTypeId: this._addressTypeId,
			addressType: this._addressType ?? RequisiteAddressType.CARD,
			comment: this._comment,
			currencyId: this._currencyId,
			isApproved: this._isApproved,
			isDefault: this._isDefault,
			createdAt: this._createdAt,
			psystem: this._psystem,
		});
	}

	get psystem(): RequisitePsystem | null {
		return this._psystem;
	}

	get createdAt(): string | null {
		return this._createdAt;
	}

	get isDefault(): boolean {
		return this._isDefault;
	}

	get isApproved(): boolean {
		return this._isApproved;
	}

	get comment(): string {
		return this._comment;
	}

	get currencyId(): number | null {
		return this._currencyId;
	}

	get addressType(): RequisiteAddressType | null {
		return this._addressType;
	}

	get addressTypeId(): number | null {
		return this._addressTypeId;
	}

	get address(): string {
		return this._address;
	}

	get name(): string {
		return this._name;
	}

	get id(): number | null {
		return this._id;
	}

	set psystem(value: RequisitePsystem) {
		this._psystem = value;
	}

	set createdAt(value: string) {
		this._createdAt = value;
	}

	set isDefault(value: boolean) {
		this._isDefault = value;
	}

	set isApproved(value: boolean) {
		this._isApproved = value;
	}

	set currencyId(value: number) {
		this._currencyId = value;
	}

	set comment(value: string) {
		this._comment = value;
	}

	set addressType(value: RequisiteAddressType) {
		this._addressType = value;
	}

	set addressTypeId(value: number) {
		this._addressTypeId = value;
	}

	set address(value: string) {
		this._address = value;
	}

	set name(value: string) {
		this._name = value;
	}

	set id(value: number) {
		this._id = value;
	}
}
